import React, { useState } from "react";
import AuthLayout from "../../layout/AuthLayout";
import {
  Backdrop,
  Box,
  CircularProgress,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { CustomPasswordInput } from "../../components/Common/CustomInputs";
import { LockResetOutlined, LogoutOutlined } from "@mui/icons-material";
import { CustomBtn } from "../../components/Common/CustomBtn";
import axiosInstance from "../../utils/config/config";
import { ErrorSnackbar } from "../../components/Common/CustomSnackbar";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { getUserById, clearUser } from "../../redux/actions/user.dispatcher";

const ForceChangePassword = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const mediumScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorState, setErrorState] = useState({
    error: false,
    errorMsg: "",
  });

  const handleLogout = () => {
    localStorage.removeItem("token");
    dispatch(clearUser());
    navigate("/login");
  };

  const passwordRegex =
    /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/;

  const handleSubmit = async () => {
    setErrorState({ error: false, errorMsg: "" });

    if (!newPassword || !confirmPassword) {
      setErrorState({
        error: true,
        errorMsg: "Please fill in both password fields.",
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorState({
        error: true,
        errorMsg: "Passwords do not match.",
      });
      return;
    }

    if (!passwordRegex.test(newPassword)) {
      setErrorState({
        error: true,
        errorMsg:
          "Password must be at least 8 characters and include an uppercase letter, a number, and a special character.",
      });
      return;
    }

    try {
      setLoading(true);
      await axiosInstance.post("/user/force-change-password", {
        newPassword,
      });

      // Refresh user data so mustChangePassword is updated in Redux
      await dispatch(getUserById());
      navigate("/home");
    } catch (error) {
      setErrorState({
        error: true,
        errorMsg:
          error.response?.data?.message || "Failed to change password.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <Box
        sx={{ px: 4, width: "100%" }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            handleSubmit();
          }
        }}
      >
        <Typography
          sx={{
            fontFamily: "'Gotham', sans-serif",
            fontSize: "36px",
            color: theme.palette.text.primary,
          }}
        >
          Change Your Password
        </Typography>
        <Typography
          sx={{
            fontFamily: "'Quicksand', sans-serif",
            fontSize: "18px",
            color: theme.palette.text.primary,
            mt: 1,
          }}
        >
          For your security, please set a new password before continuing.
          Your password must be at least 8 characters and include an uppercase
          letter, a number, and a special character.
        </Typography>

        <Box sx={{ my: 4, width: "100%" }}>
          <CustomPasswordInput
            placeholder="New Password"
            value={newPassword}
            setValue={setNewPassword}
            width={"100%"}
          />
          <CustomPasswordInput
            placeholder="Confirm New Password"
            value={confirmPassword}
            setValue={setConfirmPassword}
            width={"100%"}
          />
        </Box>

        <CustomBtn
          text="Set Password"
          Icon={LockResetOutlined}
          width={mediumScreen ? "60%" : "40%"}
          onClick={handleSubmit}
        />

        <Typography
          onClick={handleLogout}
          sx={{
            fontFamily: "'Quicksand', sans-serif",
            fontSize: "16px",
            color: theme.palette.text.primary,
            textDecorationLine: "underline",
            cursor: "pointer",
            mt: 3,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <LogoutOutlined sx={{ fontSize: "16px" }} />
          Log out
        </Typography>
      </Box>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <ErrorSnackbar
        error={errorState.error}
        handleCloseError={() => setErrorState({ error: false, errorMsg: "" })}
        errorMsg={errorState.errorMsg}
      />
    </AuthLayout>
  );
};

export default ForceChangePassword;
